/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ACTION_VIEW, SortingMode } from '@veeam-vspc/components';
import { capitalize, deepCopy } from '@veeam-vspc/core';

import type { BaseSuccessRequestResponse } from '@veeam-vspc/core';
import type { WizardStepData, GridStore } from '@veeam-vspc/components';

import { AdvancedGrid } from 'views/components/AdvancedGrid';
import { SidePanelForm } from 'components/layouts/SidePanelForm';
import { useLang } from 'views/providers/LangProvider/hooks';
import { useCompanyWizardStore } from '../../../../hooks';
import { syncHwPlansToNetwork } from '../../../../utils';
import { getWanAcceleratorColumn } from '../../utils';
import { ActionsToolbar } from './components/ActionsToolbar';

import type { ReplicaResource } from 'core/interfaces';
import type { CompanyData } from '../../../../interfaces';

export interface BackupResourcesProps extends WizardStepData<CompanyData> {
    hidePanel: () => void;
}

export const ReplicaResources = observer((props: BackupResourcesProps) => {
    const lang = useLang();
    const { hidePanel, data, onDataChange } = props;
    const api = useRef<GridStore<ReplicaResource, void, void>>();
    const wizardStore = useCompanyWizardStore();

    const [hwPlans] = useState(data.hwPlans ? deepCopy(data.hwPlans) : []);

    useEffect(() => {
        wizardStore.wanAcceleratorsCache.load({ cloudConnectAgentUid: data.cloudConnectAgentUid });
    }, []);

    return (
        <SidePanelForm
            onRequestClose={hidePanel}
            title={capitalize(lang.REPLICATION_RESOURCES)}
            description={lang.SELECT_ONE_OR_MORE_HARDWARE}
            actions={[
                {
                    text: lang.APPLY,
                    onClick: () => {
                        data.hwPlans = hwPlans;
                        data.networks = syncHwPlansToNetwork(data, lang);

                        onDataChange(data);

                        hidePanel();
                    },
                },
                {
                    text: lang.CANCEL,
                    view: ACTION_VIEW.secondary,
                    onClick: hidePanel,
                },
            ]}
        >
            <AdvancedGrid <ReplicaResource, void, void>
                defaultColumnWidth={100}
                columns={[
                    { field: 'cloudConnectResourceName', title: lang.NAME },
                    getWanAcceleratorColumn(wizardStore, lang),
                ]}
                data={() => Promise.resolve({
                    data: hwPlans,
                    meta: {
                        pagingInfo: {
                            total: hwPlans?.length,
                        },
                    },
                } as BaseSuccessRequestResponse<ReplicaResource[]>)}
                toggleable={false}
                disablePagination={true}
                disableAutoUpdate={true}
                selection={{
                    field: 'cloudConnectResourceUid',
                    multiple: false,
                    checkbox: false,
                }}
                toolbars={[
                    () => <ActionsToolbar hwPlans={hwPlans} cloudConnectAgentUid={data.cloudConnectAgentUid} />,
                ]}
                api={api}
                sortingMode={SortingMode.Local}
                isLoading={wizardStore.wanAcceleratorsCache.loading}
            />
        </SidePanelForm>
    );
});
